import type { Investor } from '@/types';

export interface FounderProfile {
  stage?: string;
  industries?: string[];
  funding_amount?: string;
  location?: string;
  business_description?: string;
}

export interface ScoredInvestor extends Investor {
  matchScore: number;
  matchReasons: string[];
}

// Strategy interface — swap RuleBasedScoring for EmbeddingScoring (Option B) without changing callers
export interface ScoringStrategy {
  score(investor: Investor, profile: FounderProfile): { score: number; reasons: string[] };
}

export class RuleBasedScoring implements ScoringStrategy {
  score(investor: Investor, profile: FounderProfile): { score: number; reasons: string[] } {
    let score = 0;
    const reasons: string[] = [];

    // Stage match — 40 pts
    if (profile.stage) {
      const founderStage = profile.stage.toLowerCase();
      const invStage = investor.stage.toLowerCase();
      const stageTokens = invStage.split(/[\s,/]+/);
      if (stageTokens.some(t => t.includes(founderStage) || founderStage.includes(t))) {
        score += 40;
        reasons.push('Stage match');
      }
    }

    // Industry alignment — up to 35 pts
    if (profile.industries?.length) {
      const founderInds = profile.industries.map(i => i.toLowerCase());
      const invInds = investor.industries.map(i => i.toLowerCase());
      const matchCount = founderInds.filter(f =>
        invInds.some(inv => inv.includes(f) || f.includes(inv))
      ).length;
      const pts = Math.round((matchCount / Math.max(founderInds.length, 1)) * 35);
      score += pts;
      if (pts > 0) reasons.push('Industry alignment');
    }

    // Check size vs raise amount — 15 pts
    if (profile.funding_amount && investor.checkSize) {
      if (checkSizeOverlaps(investor.checkSize, profile.funding_amount)) {
        score += 15;
        reasons.push('Check size fits your raise');
      }
    }

    // Contact reachability bonus — 10 pts (keeps quality investors ranked higher)
    const hasEmail = !!investor.contactInfo && !investor.contactInfo.includes('@example');
    const hasLinkedIn = !!investor.linkedin;
    if (hasEmail) score += 7;
    if (hasLinkedIn) score += 3;

    return { score, reasons };
  }
}

function parseAmount(s: string): number {
  const clean = s.replace(/[,$\s]/g, '');
  const match = clean.match(/([\d.]+)([KkMm]?)/);
  if (!match) return 0;
  const num = parseFloat(match[1]);
  const unit = match[2].toUpperCase();
  if (unit === 'M') return num * 1_000_000;
  if (unit === 'K') return num * 1_000;
  return num;
}

function checkSizeOverlaps(checkSize: string, raiseAmount: string): boolean {
  const raise = parseAmount(raiseAmount);
  if (!raise) return false;
  const parts = checkSize.split('-').map(parseAmount);
  const min = parts[0] ?? 0;
  const max = parts[1] ?? parts[0] ?? Infinity;
  // Raise should sit between 30% of check min and 3x check max
  return raise >= min * 0.3 && raise <= max * 3;
}

const DEFAULT_STRATEGY: ScoringStrategy = new RuleBasedScoring();

export function scoreAndRankInvestors(
  investors: Investor[],
  profile: FounderProfile,
  strategy: ScoringStrategy = DEFAULT_STRATEGY,
): ScoredInvestor[] {
  return investors
    .map(inv => {
      const { score, reasons } = strategy.score(inv, profile);
      return { ...inv, matchScore: score, matchReasons: reasons };
    })
    .sort((a, b) => b.matchScore - a.matchScore);
}

import { PLAN_PRICING } from '@/lib/pricing';

export const PLAN_MATCH_LIMITS: Record<string, number> = Object.fromEntries(
  Object.entries(PLAN_PRICING).map(([key, plan]) => [key, plan.matchLimit])
);

export const MAX_REFRESHES = 3;
